import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, 
  TableHead, TableRow, Chip, Alert, CircularProgress, Tooltip, IconButton,
  TablePagination, TextField, MenuItem
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import api from '../../api';

const monthOrder = {
  "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
  "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
};

const DataSheetReport = () => {
  const [datasheets, setDatasheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); 
  const [notification, setNotification] = useState({ show: false, msg: '', type: 'success' });

  // Pagination & Filters
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterMonth, setFilterMonth] = useState('All');
  const [filterYear, setFilterYear] = useState('All');

  const fetchDatasheets = async () => {
    try {
      setLoading(true);
      const res = await api.get('/payroll/datasheets'); 
      
      // Sort Chronologically descending (Year, then Month)
      const sortedData = (res.data || []).sort((a, b) => {
        if (a.payroll_year !== b.payroll_year) return b.payroll_year - a.payroll_year;
        return monthOrder[b.payroll_month] - monthOrder[a.payroll_month];
      });
      setDatasheets(sortedData);
    } catch (err) {
      showNotif('Failed to load DataSheets.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDatasheets(); }, []);

  const showNotif = (msg, type = 'success') => {
    setNotification({ show: true, msg, type });
    setTimeout(() => setNotification({ show: false, msg: '', type: 'success' }), 5000);
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const res = await api.get(`/payroll/datasheets/download/${fileId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', fileName);
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) {
      showNotif('Failed to download the file.', 'error');
    }
  };

  const handleRegenerate = async (month, year) => {
    try {
      setActionLoading(`${month}-${year}`);
      await api.post('/payroll/datasheets/regenerate', { month, year });
      showNotif(`Successfully regenerated DataSheet for ${month} ${year}.`);
      fetchDatasheets(); 
    } catch (err) {
      showNotif(err.response?.data?.detail || 'Failed to regenerate DataSheet.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // Filter Logic
  const filteredSheets = datasheets.filter(s => {
    const matchMonth = filterMonth === 'All' || s.payroll_month === filterMonth;
    const matchYear = filterYear === 'All' || s.payroll_year.toString() === filterYear;
    return matchMonth && matchYear;
  });

  const paginatedSheets = filteredSheets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Dynamic dropdown options
  const uniqueYears = [...new Set(datasheets.map(s => s.payroll_year))];
  const uniqueMonths = [...new Set(datasheets.map(s => s.payroll_month))];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {notification.show && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.msg}</Alert>}

      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>Official Payroll DataSheets</Typography>
            <Typography variant="body2" color="textSecondary">Download or regenerate Excel snapshots.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select size="small" label="Filter Year" value={filterYear} onChange={(e) => {setFilterYear(e.target.value); setPage(0);}} sx={{ width: 120 }}>
              <MenuItem value="All">All</MenuItem>
              {uniqueYears.map(y => <MenuItem key={y} value={y.toString()}>{y}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Filter Month" value={filterMonth} onChange={(e) => {setFilterMonth(e.target.value); setPage(0);}} sx={{ width: 140 }}>
              <MenuItem value="All">All</MenuItem>
              {uniqueMonths.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>File Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Generated By</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: '120px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSheets.map((sheet) => (
                  <TableRow key={sheet.id} hover>
                    <TableCell><strong>{sheet.payroll_month} {sheet.payroll_year}</strong></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{sheet.file_name}</Typography></TableCell>
                    <TableCell><Chip label={sheet.status} size="small" color={sheet.status === 'Active' ? 'success' : 'default'} variant="outlined" /></TableCell>
                    <TableCell>{sheet.generated_by_name || sheet.generated_by}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title="Download Excel" arrow>
                          <IconButton size="small" color="primary" onClick={() => handleDownload(sheet.id, sheet.file_name)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {sheet.status === 'Active' && (
                          <Tooltip title="Regenerate DataSheet to reflect recent Hold Salary changes" arrow>
                            <span>
                              <IconButton size="small" color="warning" onClick={() => handleRegenerate(sheet.payroll_month, sheet.payroll_year)} disabled={actionLoading === `${sheet.payroll_month}-${sheet.payroll_year}`}>
                                {actionLoading === `${sheet.payroll_month}-${sheet.payroll_year}` ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={filteredSheets.length} page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default DataSheetReport;